import { School } from "@mui/icons-material";
import { Box } from "@mui/material";
import { useState } from "react";

import { CMS_MEDIA_BASE_URL } from "../config";
import { borderRadius, color } from "../theme/designTokens";
import { ContentKindIcon } from "./ContentKindIcon";

type ActivityThumbnailProps = {
  // CMS thumbnail filename (resolved server-side); null falls back to the content-kind icon.
  thumbnailFilename: string | null;
  cmsContentKind: string | null;
  width?: number;
  height?: number;
  iconSize?: number;
  resolveThumbnailUrl?: (filename: string) => string;
};

// Shared activity preview tile: shows the real CMS thumbnail image when available,
// otherwise the content-kind icon on a neutral tile. Used by the recommended-activities
// rows and the procedure activity session cards so both show the same real artwork.
export const ActivityThumbnail = ({
  thumbnailFilename,
  cmsContentKind,
  width = 76,
  height = 54,
  iconSize = 24,
  resolveThumbnailUrl,
}: ActivityThumbnailProps) => {
  let thumbnailUrl: string | null = null;
  if (thumbnailFilename) {
    try {
      thumbnailUrl = resolveThumbnailUrl
        ? resolveThumbnailUrl(thumbnailFilename)
        : `${CMS_MEDIA_BASE_URL}/${thumbnailFilename}`;
    } catch {
      thumbnailUrl = null;
    }
  }
  const [failedThumbnailUrl, setFailedThumbnailUrl] = useState<string | null>(null);
  const visibleThumbnailUrl = thumbnailUrl !== failedThumbnailUrl ? thumbnailUrl : null;

  return (
    <Box
      sx={{
        width,
        height,
        borderRadius: borderRadius.sm,
        border: `1px solid ${color.datagridBorder}`,
        backgroundColor: color.background2,
        display: "grid",
        placeItems: "center",
        flex: `0 0 ${width}px`,
        overflow: "hidden",
      }}
    >
      {visibleThumbnailUrl ? (
        <Box
          component="img"
          data-testid="activity-thumbnail-image"
          src={visibleThumbnailUrl}
          alt=""
          onError={() => setFailedThumbnailUrl(visibleThumbnailUrl)}
          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", borderRadius: borderRadius.sm }}
        />
      ) : cmsContentKind ? (
        <ContentKindIcon contentKind={cmsContentKind} size={iconSize} />
      ) : (
        <School data-testid="activity-thumbnail-fallback" sx={{ color: color.iconMuted, fontSize: iconSize }} />
      )}
    </Box>
  );
};
